import * as store from './store';
import { getConnectionStatus, markNoAnswer, subscribeConnection } from '../connectionStatus';

// Keeps the app usable in a sports hall with no signal.
//
// Reads: every API GET that succeeds is saved on the phone (per user). If the
// server can't be reached, the saved copy is returned instead, so pages that
// were opened before still open.
//
// Writes: a few in-hall actions (skill and routine progress updates, register marks) are
// saved on the phone when they can't be sent, and sent once the connection is
// back. These only ever set something to a state, so sending one twice is
// harmless. Waiting changes are laid over saved and fresh data alike, so updates
// don't disappear when a page reloads.
//
// Records: training diary entries and gymnast routines can also be added,
// edited and deleted with no signal. A new one gets its id on the phone, and the
// server makes one record however many times that id is sent. Edits to a record
// that hasn't been sent yet fold into it; deleting it just forgets it. If the
// server refuses one, it stays on the phone marked as not saved, so nothing
// typed is lost, until it is fixed or discarded.
//
// Ready for the hall: after sign-in, and whenever the connection comes back,
// the training diary, the gymnast's routines and the skill library are fetched
// and saved, so they work with no signal even if they were never opened first.
// Coaches also get today's registers, and the page and routines of every
// gymnast in today's sessions.

const GET_FALLBACK_MS = 8000;   // give up waiting and show the saved copy
const WRITE_TIMEOUT_MS = 10000; // give up waiting and keep the change for later
const MAX_SAVED_READS = 500;
const MAX_ATTEMPTS = 3;         // server errors before a waiting change is given up on
const RETRY_MS = 30000;
// 503 rounds in a row (about 5 minutes at RETRY_MS) before saying the server
// is unavailable. Changes are never dropped for a 503, so this is the only
// sign that they are stuck.
const UNAVAILABLE_NOTICE_RUNS = 10;
const READY_DELAY_MS = 3000;            // let the page being opened load first
const READY_EVERY_MS = 30 * 60 * 1000;
const STAFF_ROLES = ['CLUB_ADMIN', 'COACH', 'WELFARE'];
const REGISTER_ROLES = ['CLUB_ADMIN', 'COACH'];
const READY_AT_ONCE = 4;               // requests in flight while saving a session's gymnasts

// The routes below (and RECORDS) are retried for ever on a 503, which is
// only ever transient (the tenant and auth checks while the database is
// unreachable, or Render's proxy while the backend restarts). They must
// never answer 503 for a deterministic reason (an unconfigured service, say):
// such a change would wait on the phone for ever.
const QUEUEABLE = [
  {
    kind: 'skill', method: 'post', re: /\/api\/progress\/skill$/,
    key: b => `skill:${b.gymnastId}:${b.skillId}:${b.apparatus || 'TRA'}`,
    // Record when it was actually marked, not when it was sent
    stamp: b => (b.status === 'COMPLETED' && !b.completedAt ? { ...b, completedAt: new Date().toISOString() } : b),
  },
  { kind: 'routine', method: 'put', re: /\/api\/progress\/routine$/, key: b => `routine:${b.gymnastId}:${b.routineId}` },
  { kind: 'attendance', method: 'post', re: /\/api\/booking\/attendance\/([^/?]+)$/, key: (b, m) => `att:${m[1]}:${b.gymnastId}` },
  { kind: 'attendance', method: 'delete', re: /\/api\/booking\/attendance\/([^/?]+)\/([^/?]+)$/, key: (b, m) => `att:${m[1]}:${m[2]}` },
];

// Records made, changed or deleted with no signal. `create` holds the gymnast
// id; `item` is the record's own id. `editPath` is where changes to a record go.
const RECORDS = [
  {
    kind: 'diary',
    create: /\/api\/training-diary\/gymnasts\/([^/?]+)\/entries$/,
    item: /\/api\/training-diary\/entries\/([^/?]+)$/,
    editPath: id => `/api/training-diary/entries/${id}`,
  },
  {
    kind: 'gymnastRoutine',
    create: /\/api\/gymnast-routines\/gymnast\/([^/?]+)$/,
    item: /\/api\/gymnast-routines\/(?!gymnast$)([^/?]+)$/,
    editPath: id => `/api/gymnast-routines/${id}`,
  },
];

const KIND_LABEL = {
  skill: 'A skill progress update', routine: 'A routine progress update', attendance: 'A register mark',
  diary: 'A diary entry', gymnastRoutine: 'A routine',
};

// Shaped like Prisma's cuid(), which the server checks for
export function newRecordId() {
  const chars = '0123456789abcdefghijklmnopqrstuvwxyz';
  const bytes = new Uint8Array(24);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  return `c${Array.from(bytes, b => chars[b % 36]).join('')}`;
}

let uid = null;
let role = null;
let readyAt = 0;              // when the pages for the hall were last saved
let readying = false;
let readyTimer = null;
let pending = new Map();      // key -> queued change, for the signed-in user
let failures = [];            // changes the server refused when they were sent
let savedCopyAt = null;       // when the saved data on screen was fetched
let sentCount = 0;            // changes sent since the last time the queue was empty
let flushing = false;
let readWrites = 0;
let retryTimer = null;
let unavailableRuns = 0;      // flush runs in a row stopped by a 503
const listeners = new Set();

const readKey = url => `r:${uid}:${url}`;
const queueKey = key => `q:${uid}:${key}`;

function notify() {
  const state = getOfflineState();
  listeners.forEach(fn => fn(state));
}

const waiting = () => [...pending.values()].filter(e => !e.failed);

// pending: changes waiting to send. kept: records the server refused, kept on
// the phone until they're fixed or discarded.
export function getOfflineState() {
  const all = [...pending.values()];
  return {
    pending: all.filter(e => !e.failed).length, kept: all.filter(e => e.failed).length, failures, savedCopyAt, sentCount, flushing,
    // The server has answered 503 for a while: changes are waiting on the phone
    serverUnavailable: unavailableRuns >= UNAVAILABLE_NOTICE_RUNS && all.some(e => !e.failed),
  };
}

export function subscribeOffline(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Called with a record kind ('diary', 'gymnastRoutine') once waiting changes
// of that kind have been sent, so lists can swap their copies for the real ones
const sentListeners = new Set();
export function onRecordsSent(fn) {
  sentListeners.add(fn);
  return () => sentListeners.delete(fn);
}

export function dismissFailures() {
  failures = [];
  notify();
}

export function clearSavedCopyNotice() {
  savedCopyAt = null;
  sentCount = 0;
  notify();
}

// Called by AuthContext whenever the signed-in user changes
export async function setOfflineUser(id, userRole) {
  if (id === uid) return;
  uid = id || null;
  role = userRole || null;
  readyAt = 0;
  scheduleReady();
  pending = new Map();
  failures = [];
  savedCopyAt = null;
  unavailableRuns = 0;
  if (uid) {
    const queued = await store.entries(`q:${uid}:`).catch(() => []);
    if (uid !== id) return;
    queued.forEach(([, entry]) => pending.set(entry.key, entry));
  }
  notify();
  if (waiting().length && getConnectionStatus() !== 'offline') flush();
}

// On sign-out: forget this user's saved pages. Unsent changes are kept and
// sent next time the same person signs in on this phone.
export async function forgetSavedPages(id) {
  if (!id) return;
  await store.clear(`r:${id}:`).catch(() => {});
}

export const pendingCount = () => pending.size;

// Forget a waiting or not-saved record change (the Discard button)
export async function discardWaiting(kind, id) {
  await unqueue(`${kind}:${id}`);
  notify();
}

// Admin > Clear cache: forget everything saved on this phone except the login
// and any changes still waiting to send, then reload with fresh copies.
export async function clearPhoneCache() {
  await store.clear('r:').catch(() => {});
  await store.clear('lv:').catch(() => {});
  savedCopyAt = null;
  notify();
  if (typeof caches !== 'undefined') {
    const names = await caches.keys().catch(() => []);
    await Promise.all(names.filter(n => n.startsWith('tl-')).map(n => caches.delete(n)));
  }
  const registration = await navigator.serviceWorker?.getRegistration?.().catch(() => null);
  await registration?.update().catch(() => {});
}

// ── Matching requests ─────────────────────────────────────────────────────────

function fullUrl(config) {
  const base = config.baseURL && !/^https?:/.test(config.url) ? config.baseURL.replace(/\/$/, '') : '';
  let url = `${base}${config.url}`;
  if (config.params && Object.keys(config.params).length) {
    const qs = new URLSearchParams();
    Object.keys(config.params).sort().forEach(k => {
      if (config.params[k] !== undefined && config.params[k] !== null) qs.append(k, config.params[k]);
    });
    const s = qs.toString();
    if (s) url += (url.includes('?') ? '&' : '?') + s;
  }
  return url;
}

const pathOf = url => url.replace(/^https?:\/\/[^/]+/, '').split('?')[0];

// Adapters return the raw body; axios only parses JSON after this point
function asJson(data) {
  if (typeof data !== 'string') return data;
  try { return JSON.parse(data); } catch { return data; }
}

function parseBody(data) {
  if (!data) return {};
  if (typeof data === 'string') {
    try { return JSON.parse(data); } catch { return {}; }
  }
  return data;
}

function queueRuleFor(config) {
  const method = (config.method || 'get').toLowerCase();
  const path = pathOf(fullUrl(config));
  for (const rule of QUEUEABLE) {
    const m = rule.method === method && path.match(rule.re);
    if (m) return { rule, match: m };
  }
  return null;
}

const isCachableGet = config =>
  (config.method || 'get').toLowerCase() === 'get'
  && (!config.responseType || config.responseType === 'json')
  && pathOf(fullUrl(config)).startsWith('/api/')
  && !config.offlineSkip;

const noAnswer = err => !err.response && err.code !== 'ERR_CANCELED';

// ── Laying waiting changes over data ─────────────────────────────────────────

function skillFromLevels(levels, skillId) {
  for (const level of levels || []) {
    const skill = (level.skills || []).find(s => s.id === skillId);
    // Match what the server sends: only skills linked by levelId carry their level
    if (skill) return { ...skill, level: skill.levelId === level.id ? { id: level.id, identifier: level.identifier, name: level.name } : null };
  }
  return { id: skillId, level: null };
}

function routineFromLevels(levels, routineId) {
  for (const level of levels || []) {
    const routine = (level.routines || []).find(r => r.id === routineId);
    if (routine) return { ...routine, level: { id: level.id, identifier: level.identifier, name: level.name } };
  }
  return { id: routineId };
}

async function savedLevels() {
  const saved = await store.get(`lv:${uid}`).catch(() => null);
  return saved || [];
}

export async function overlay(path, data) {
  if (!pending.size || !data || typeof data !== 'object') return data;
  const changes = [...pending.values()];

  const gymnast = path.match(/^\/api\/gymnasts\/([^/]+)$/);
  if (gymnast && Array.isArray(data.skillProgress)) {
    const mine = changes.filter(c => c.body.gymnastId === gymnast[1]);
    if (!mine.length) return data;
    const levels = await savedLevels();
    const out = { ...data, skillProgress: [...data.skillProgress], routineProgress: [...(data.routineProgress || [])] };
    mine.forEach(c => {
      if (c.kind === 'skill') {
        const apparatus = c.body.apparatus || 'TRA';
        const i = out.skillProgress.findIndex(sp => (sp.skill?.id || sp.skillId) === c.body.skillId && (sp.apparatus || 'TRA') === apparatus);
        const next = { ...(i >= 0 ? out.skillProgress[i] : { skillId: c.body.skillId, apparatus, skill: skillFromLevels(levels, c.body.skillId) }), status: c.body.status, completedAt: c.body.completedAt || null, pending: true };
        if (i >= 0) out.skillProgress[i] = next; else out.skillProgress.push(next);
      } else if (c.kind === 'routine') {
        const i = out.routineProgress.findIndex(rp => (rp.routine?.id || rp.routineId) === c.body.routineId);
        const next = { ...(i >= 0 ? out.routineProgress[i] : { routineId: c.body.routineId, routine: routineFromLevels(levels, c.body.routineId) }), status: c.body.status, pending: true };
        if (i >= 0) out.routineProgress[i] = next; else out.routineProgress.push(next);
      }
    });
    return out;
  }

  const diaryList = path.match(/^\/api\/training-diary\/gymnasts\/([^/]+)\/entries$/);
  if (diaryList && Array.isArray(data.entries)) {
    const entries = overlayRecords(data.entries, changes, 'diary', diaryList[1])
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    return { ...data, entries };
  }

  const routineList = path.match(/^\/api\/gymnast-routines\/gymnast\/([^/]+)$/);
  if (routineList && Array.isArray(data)) return overlayRecords(data, changes, 'gymnastRoutine', routineList[1]);

  if (/^\/api\/booking\/attendance\/[^/]+$/.test(path) && Array.isArray(data.attendees)) {
    return {
      ...data,
      attendees: data.attendees.map(a => {
        const c = pending.get(`att:${a.sessionInstanceId}:${a.gymnastId}`);
        if (!c) return a;
        return { ...a, status: c.method === 'delete' ? 'UNMARKED' : c.body.status, pending: true };
      }),
    };
  }

  return data;
}

// Lay waiting record changes over a list of records from the server
function overlayRecords(list, changes, kind, gymnastId) {
  const mine = changes.filter(c => c.kind === kind && c.record);
  if (!mine.length) return list;
  const byId = new Map(mine.map(c => [c.id, c]));
  const out = list
    .filter(r => byId.get(r.id)?.op !== 'delete')
    .map(r => {
      const c = byId.get(r.id);
      return c?.preview ? { ...r, ...c.preview, id: r.id, pending: !c.failed, failed: c.failed || null } : r;
    });
  const listed = new Set(list.map(r => r.id));
  mine
    .filter(c => c.op === 'create' && c.gymnastId === gymnastId && !listed.has(c.id))
    .sort((a, b) => a.queuedAt - b.queuedAt)
    .forEach(c => out.push({ ...c.preview, id: c.id, unsent: true, pending: !c.failed, failed: c.failed || null }));
  return out;
}

// ── Saved reads ──────────────────────────────────────────────────────────────

async function saveRead(url, data) {
  if (!uid) return;
  const savedAt = Date.now();
  await store.set(readKey(url), { data, savedAt }).catch(() => {});
  if (/^\/api\/levels$/.test(pathOf(url)) && Array.isArray(data)) {
    await store.set(`lv:${uid}`, data).catch(() => {});
  }
  readWrites += 1;
  if (readWrites % 50 === 0) pruneReads();
}

async function pruneReads() {
  const all = await store.entries(`r:${uid}:`).catch(() => []);
  if (all.length <= MAX_SAVED_READS) return;
  all.sort((a, b) => a[1].savedAt - b[1].savedAt)
    .slice(0, all.length - MAX_SAVED_READS)
    .forEach(([k]) => store.del(k).catch(() => {}));
}

async function savedResponse(config, url) {
  if (!uid) return null;
  const saved = await store.get(readKey(url)).catch(() => null);
  if (!saved) return null;
  savedCopyAt = savedCopyAt ? Math.min(savedCopyAt, saved.savedAt) : saved.savedAt;
  notify();
  return {
    data: await overlay(pathOf(url), saved.data),
    status: 200, statusText: 'OK', headers: {}, config, request: null,
    offline: 'saved', savedAt: saved.savedAt,
  };
}

// ── Queue ────────────────────────────────────────────────────────────────────

async function enqueue(config, rule, match) {
  const body = (rule.stamp || (b => b))(parseBody(config.data));
  const key = rule.key(body, match);
  const entry = {
    key, kind: rule.kind, method: rule.method, url: fullUrl(config), body, queuedAt: Date.now(),
  };
  pending.set(key, entry);
  await store.set(queueKey(key), entry).catch(() => {});
  notify();
  return { data: null, status: 202, statusText: 'Queued', headers: {}, config, request: null, offline: 'queued' };
}

async function unqueue(key) {
  if (!pending.has(key)) return;
  pending.delete(key);
  await store.del(queueKey(key)).catch(() => {});
}

async function keep(entry) {
  pending.set(entry.key, entry);
  await store.set(queueKey(entry.key), entry).catch(() => {});
  notify();
}

// Fields the edit routes don't accept
const forEdit = body => {
  const { id, levelRoutineId, ...rest } = body || {};
  return rest;
};

async function sendEntry(entry) {
  if (entry.op === 'create') {
    const res = await sendRequest({ method: 'post', url: entry.url, body: entry.body });
    // The server already had it (an earlier send landed but the reply was
    // lost), so it ignored this copy: send the later edits on their own.
    if (res?.status === 200 && entry.edited) await sendRequest({ method: 'put', url: entry.editUrl, body: forEdit(entry.body) });
    return;
  }
  await sendRequest(entry);
}

let sendRequest = null; // set by installOffline: sends a request bypassing the queue
export const setSenderForTests = fn => { sendRequest = fn; };

export async function flush() {
  if (flushing || !pending.size || !uid || !sendRequest) return;
  flushing = true;
  notify();
  const owner = uid;
  const sentKinds = new Set();
  let stopped = false; // no signal, signed out or a server hiccup: wait before trying again
  try {
    const queue = waiting().sort((a, b) => a.queuedAt - b.queuedAt);
    for (const queued of queue) {
      if (uid !== owner) { stopped = true; break; } // never send one person's changes as someone else
      let entry = queued;
      if (entry.op === 'create' && !entry.tried) {
        // From here the server may have it even if no reply comes back
        entry = { ...entry, tried: true };
        await keep(entry);
      }
      try {
        await sendEntry(entry);
        unavailableRuns = 0;
        // Changed while it was sending: the newer version goes next time
        if (pending.get(entry.key) === entry) await unqueue(entry.key);
        sentCount += 1;
        if (entry.record) sentKinds.add(entry.kind);
      } catch (err) {
        const status = err.response?.status;
        if (!err.response || status === 401) { stopped = true; break; } // no signal, or signed out: keep it and try later
        if (status === 503) {
          // Server briefly unavailable (e.g. its database): keep it, never
          // count it as a failed attempt, and try again shortly. After
          // UNAVAILABLE_NOTICE_RUNS of these in a row, the indicator says so.
          unavailableRuns += 1;
          clearTimeout(retryTimer);
          retryTimer = setTimeout(flush, RETRY_MS);
          stopped = true;
          break;
        }
        unavailableRuns = 0; // the server answered something other than 503
        if (pending.get(entry.key) !== entry) continue; // changed while sending: try the new version
        if (entry.op === 'delete' && status === 404) {
          // Already gone
          await unqueue(entry.key);
          sentCount += 1;
          sentKinds.add(entry.kind);
          notify();
          continue;
        }
        const attempts = (entry.attempts || 0) + 1;
        if ((status >= 500 || status === 429) && attempts < MAX_ATTEMPTS) {
          // A server hiccup: try again shortly
          await keep({ ...entry, attempts });
          clearTimeout(retryTimer);
          retryTimer = setTimeout(flush, RETRY_MS);
          stopped = true;
          break;
        }
        const reason = err.response.data?.error || 'the server refused it';
        if (entry.record) {
          // Keep what was typed so it can be fixed or discarded
          await keep({ ...entry, failed: reason, attempts: 0 });
          failures = [...failures, `${KIND_LABEL[entry.kind]} couldn't be saved: ${reason}. It's kept on this phone so you can fix it or discard it.`];
        } else {
          await unqueue(entry.key);
          failures = [...failures, `${KIND_LABEL[entry.kind] || 'A change'} wasn't saved: ${reason}.`];
        }
      }
      notify();
    }
  } finally {
    flushing = false;
    notify();
    sentKinds.forEach(kind => sentListeners.forEach(fn => fn(kind)));
    // Changes made while this was sending go now, not at the next reconnect
    if (!stopped && uid === owner && waiting().length) setTimeout(flush, 0);
  }
}

// ── Records ──────────────────────────────────────────────────────────────────

function recordRuleFor(config) {
  const method = (config.method || 'get').toLowerCase();
  const path = pathOf(fullUrl(config));
  for (const rule of RECORDS) {
    const created = method === 'post' && path.match(rule.create);
    if (created) return { rule, op: 'create', gymnastId: created[1] };
    const item = (method === 'put' || method === 'delete') && path.match(rule.item);
    if (item) return { rule, op: method === 'put' ? 'edit' : 'delete', id: item[1] };
  }
  return null;
}

const queuedReply = (config, data, status = 202) => ({ data, status, statusText: 'Queued', headers: {}, config, request: null, offline: 'queued' });

// What the page shows for a waiting record: what it said it would look like
// (config.offlinePreview), or the body sent
const previewOf = (config, body) => {
  const { id, ...fields } = body;
  return { ...fields, ...(config.offlinePreview || {}) };
};

async function recordWrite(send, config, { rule, op, gymnastId, id: itemId }) {
  const url = fullUrl(config);
  const origin = (url.match(/^https?:\/\/[^/]+/) || [''])[0];
  let body = parseBody(config.data);

  if (op === 'create') {
    // Always give it an id, so a resend after a lost reply can't make a second one
    if (!body.id) body = { ...body, id: newRecordId() };
    const out = { ...config, data: JSON.stringify(body) };
    const entry = {
      key: `${rule.kind}:${body.id}`, kind: rule.kind, record: true, op, id: body.id, gymnastId,
      method: 'post', url, editUrl: `${origin}${rule.editPath(body.id)}`, body,
      preview: previewOf(config, body), queuedAt: Date.now(),
    };
    if (getConnectionStatus() === 'offline') return saveRecord(out, entry);
    try {
      return await send({ ...out, timeout: config.timeout || WRITE_TIMEOUT_MS });
    } catch (err) {
      if (!noAnswer(err)) throw err;
      markNoAnswer();
      return saveRecord(out, { ...entry, tried: true });
    }
  }

  const key = `${rule.kind}:${itemId}`;
  const earlier = pending.get(key);

  // Not on the server yet: change or forget the copy on the phone
  if (earlier?.op === 'create') {
    if (op === 'delete') {
      if (earlier.tried) {
        // It may have reached the server without a reply: delete it there too
        await keep({ key, kind: rule.kind, record: true, op: 'delete', id: itemId, method: 'delete', url: earlier.editUrl, queuedAt: Date.now() });
        if (getConnectionStatus() !== 'offline') flush();
      } else {
        await unqueue(key);
        notify();
      }
      return queuedReply(config, null, 204);
    }
    const merged = {
      ...earlier, body: { ...earlier.body, ...body, id: itemId }, edited: true, failed: null, attempts: 0,
      preview: { ...earlier.preview, ...previewOf(config, body) },
    };
    await keep(merged);
    if (getConnectionStatus() !== 'offline') flush();
    return queuedReply(config, { ...merged.preview, id: itemId, unsent: true, pending: true });
  }

  const entry = {
    key, kind: rule.kind, record: true, op, id: itemId, method: op === 'edit' ? 'put' : 'delete', url,
    body: op === 'edit' ? body : undefined, preview: op === 'edit' ? previewOf(config, body) : null, queuedAt: Date.now(),
  };
  if (getConnectionStatus() === 'offline') return saveRecord(config, entry);
  try {
    const response = await send({ ...config, timeout: config.timeout || WRITE_TIMEOUT_MS });
    await unqueue(key); // superseded by this one
    notify();
    return response;
  } catch (err) {
    if (!noAnswer(err)) throw err;
    markNoAnswer();
    return saveRecord(config, entry);
  }
}

async function saveRecord(config, entry) {
  await keep(entry);
  if (entry.op === 'delete') return queuedReply(config, null);
  return queuedReply(config, { ...entry.preview, id: entry.id, unsent: entry.op === 'create', pending: true });
}

// ── Adapter ──────────────────────────────────────────────────────────────────

function race(promise, ms) {
  let timer;
  return Promise.race([
    promise.then(r => ({ r }), e => ({ e })),
    new Promise(resolve => { timer = setTimeout(() => resolve({ timedOut: true }), ms); }),
  ]).finally(() => clearTimeout(timer));
}

export function withOffline(send) {
  return async function offlineAdapter(config) {
    if (!uid || config.offlineReplay) return send(config);

    const record = recordRuleFor(config);
    if (record) return recordWrite(send, config, record);

    const queueable = queueRuleFor(config);
    if (queueable) {
      const { rule, match } = queueable;
      if (getConnectionStatus() === 'offline') return enqueue(config, rule, match);
      try {
        const response = await send({ ...config, timeout: config.timeout || WRITE_TIMEOUT_MS });
        // A newer change went through, so an older waiting one must not overwrite it
        await unqueue(rule.key(parseBody(config.data), match));
        notify();
        return response;
      } catch (err) {
        if (!noAnswer(err)) throw err;
        markNoAnswer();
        return enqueue(config, rule, match);
      }
    }

    if (!isCachableGet(config)) return send(config);

    const url = fullUrl(config);
    const path = pathOf(url);
    if (config.offlineReady) {
      const response = await send(config);
      const data = asJson(response.data);
      if (response.status === 200 && data && typeof data === 'object') await saveRead(url, data);
      return { ...response, data };
    }
    const hasSaved = await store.get(readKey(url)).then(Boolean, () => false);

    if (hasSaved && getConnectionStatus() === 'offline') {
      // Refresh quietly in case the connection is back, but don't wait for it
      send(config).then(r => { const data = asJson(r.data); if (r.status === 200 && data && typeof data === 'object') saveRead(url, data); }).catch(() => {});
      return savedResponse(config, url);
    }

    const network = send(config).then(async response => {
      const data = asJson(response.data);
      if (response.status === 200 && data && typeof data === 'object') await saveRead(url, data);
      return { ...response, data };
    });

    const outcome = hasSaved ? await race(network, GET_FALLBACK_MS) : await network.then(r => ({ r }), e => ({ e }));
    if (outcome.timedOut) return savedResponse(config, url);
    if (outcome.e) {
      if (hasSaved && noAnswer(outcome.e)) {
        markNoAnswer();
        return savedResponse(config, url);
      }
      throw outcome.e;
    }
    const response = outcome.r;
    return { ...response, data: await overlay(path, response.data) };
  };
}

// ── Ready for the hall ───────────────────────────────────────────────────────

let fetchForLater = null; // set by installOffline
export const setReadyFetcherForTests = fn => { fetchForLater = fn; };

function scheduleReady() {
  clearTimeout(readyTimer);
  if (uid) readyTimer = setTimeout(getReady, READY_DELAY_MS);
}

export async function getReady() {
  const forUser = uid;
  if (!forUser || !fetchForLater || readying || getConnectionStatus() === 'offline') return;
  if (Date.now() - readyAt < READY_EVERY_MS) return;
  readying = true;
  const get = url => (uid === forUser ? fetchForLater(url).then(r => r.data, () => null) : Promise.resolve(null));
  try {
    const shared = ['/api/skills', '/api/levels'];
    if (STAFF_ROLES.includes(role)) shared.push('/api/booking/competition-events/set-routines');
    await Promise.all(shared.map(get));
    const diaries = await get('/api/training-diary/mine');
    const gymnastIds = new Set(Array.isArray(diaries) ? diaries.map(g => g.id) : []);
    if (role === 'CHILD') gymnastIds.add(forUser);
    for (const id of gymnastIds) {
      await Promise.all([
        diaries?.some(g => g.id === id) && get(`/api/training-diary/gymnasts/${id}/entries`),
        get(`/api/gymnast-routines/gymnast/${id}`),
      ]);
    }
    if (REGISTER_ROLES.includes(role)) await readyForTodaysSessions(get);
    if (uid === forUser) readyAt = Date.now();
  } finally {
    readying = false;
  }
}

const localDay = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

async function readyForTodaysSessions(get) {
  const now = new Date();
  // Same request as the sessions calendar and the Register menu item
  const sessions = await get(`/api/booking/sessions?month=${now.getMonth() + 1}&year=${now.getFullYear()}`);
  const today = (Array.isArray(sessions) ? sessions : [])
    .filter(s => !s.cancelledAt && String(s.date).slice(0, 10) === localDay(now));
  const gymnastIds = new Set();
  for (const session of today) {
    const register = await get(`/api/booking/attendance/${session.id}`);
    (register?.attendees || []).forEach(a => gymnastIds.add(a.gymnastId));
  }
  const ids = [...gymnastIds];
  for (let i = 0; i < ids.length; i += READY_AT_ONCE) {
    await Promise.all(ids.slice(i, i + READY_AT_ONCE).flatMap(id => [
      get(`/api/gymnasts/${id}`),
      get(`/api/gymnast-routines/gymnast/${id}`),
    ]));
  }
}

let installed = false;
export function installOffline(clients, defaultClient) {
  if (installed) return;
  installed = true;
  if (typeof defaultClient?.getAdapter !== 'function') return; // mocked in tests
  clients.forEach(client => {
    if (client?.defaults) client.defaults.adapter = withOffline(defaultClient.getAdapter(client.defaults.adapter));
  });
  sendRequest = entry => defaultClient.request({
    method: entry.method,
    url: entry.url,
    data: entry.method === 'delete' ? undefined : entry.body,
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    offlineReplay: true,
  });
  fetchForLater = url => defaultClient.get(url, { offlineReady: true });
  scheduleReady();
  subscribeConnection(status => {
    if (status !== 'offline') {
      flush();
      scheduleReady();
    } else {
      sentCount = 0;
      notify();
    }
  });
}
